import { Box, Button, Checkbox, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { KeyboardDoubleArrowDown } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { TimePicker } from '../../MUI/time-picker/TimePicker';
import { useAppThemeContext } from '../../../contexts';

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export interface IDaySchedule {
  dayOfWeek: number;
  isClosed: boolean;
  startTime?: string | null; // "HH:mm" format
  endTime?: string | null;
}

export function createDefaultWeekSchedule(): IDaySchedule[] {
  const days: IDaySchedule[] = [];
  for (let i = 0; i < 7; i++) {
    days.push({
      dayOfWeek: i,
      isClosed: false,
      startTime: '08:00',
      endTime: '18:00',
    });
  }
  return days;
}

export function scheduleToApiDto(schedule: IDaySchedule[]): { dayOfWeek: number; isClosed: boolean; startTime: string | null; endTime: string | null }[] {
  return schedule.map(d => ({
    dayOfWeek: d.dayOfWeek,
    isClosed: d.isClosed,
    startTime: d.isClosed ? null : (d.startTime || null),
    endTime: d.isClosed ? null : (d.endTime || null),
  }));
}

interface IProps {
  schedule: IDaySchedule[];
  onChange: (days: IDaySchedule[]) => void;
  defaultStartTime?: Dayjs | null;
  defaultEndTime?: Dayjs | null;
  readOnly?: boolean;
  mode?: 'simple' | 'full';
  showTitle?: boolean;
}

export const DayScheduleEditor: React.FC<IProps> = ({
  schedule,
  onChange,
  defaultStartTime,
  defaultEndTime,
  readOnly = false,
  mode = 'full',
  showTitle = true,
}) => {
  const handleDayChange = (index: number, isClosed: boolean) => {
    const newDays = [...schedule];
    newDays[index] = { ...newDays[index], isClosed };
    onChange(newDays);
  };

  const handleTimeChange = (index: number, field: 'startTime' | 'endTime', value: Dayjs | null) => {
    const newDays = [...schedule];
    newDays[index] = {
      ...newDays[index],
      [field]: value ? value.format('HH:mm') : null,
    };
    onChange(newDays);
  };

  const applyToAll = () => {
    if (!defaultStartTime || !defaultEndTime) return;
    const startStr = defaultStartTime.format('HH:mm');
    const endStr = defaultEndTime.format('HH:mm');
    const newDays = schedule.map(day => ({
      ...day,
      startTime: startStr,
      endTime: endStr,
    }));
    onChange(newDays);
  };

  const mirrorDown = (fromIndex: number) => {
    const source = schedule[fromIndex];
    const newDays = schedule.map((day, i) =>
      i > fromIndex
        ? { ...day, isClosed: source.isClosed, startTime: source.startTime, endTime: source.endTime }
        : day
    );
    onChange(newDays);
  };

  const allClosed = schedule.every(d => d.isClosed);

  const { isMobile } = useAppThemeContext();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {showTitle && (
        <Typography variant="subtitle1" fontWeight={600}>
          Horários por dia da semana
        </Typography>
      )}

      {isMobile ? (
        /* ── Mobile: card layout ── */
        <Box display='flex' flexDirection='column' gap={1.5}>
          {schedule.map((day, index) => {
            const startVal = day.startTime ? dayjs(day.startTime, 'HH:mm') : null;
            const endVal = day.endTime ? dayjs(day.endTime, 'HH:mm') : null;
            return (
              <Paper
                key={day.dayOfWeek}
                variant='outlined'
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  opacity: day.isClosed ? 0.6 : 1,
                }}
              >
                <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <Typography variant='body2' fontWeight={600}>
                      {DAY_NAMES[day.dayOfWeek]}
                    </Typography>
                    {!readOnly && index < 6 && (
                      <Tooltip title="Espelhar horário para os dias seguintes">
                        <IconButton
                          size='small'
                          onClick={() => mirrorDown(index)}
                        >
                          <KeyboardDoubleArrowDown fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <Typography variant='caption' color='text.secondary'>
                      Fechado
                    </Typography>
                    <Checkbox
                      checked={day.isClosed}
                      onChange={e => handleDayChange(index, e.target.checked)}
                      disabled={readOnly}
                      size='small'
                    />
                  </Box>
                </Box>
                {mode === 'full' && (
                  <Box display='flex' gap={1}>
                    <TimePicker
                      label="Início"
                      value={startVal}
                      onChange={(value: Dayjs | null) => handleTimeChange(index, 'startTime', value)}
                      disabled={day.isClosed || readOnly}
                    />
                    <TimePicker
                      label="Fim"
                      value={endVal}
                      onChange={(value: Dayjs | null) => handleTimeChange(index, 'endTime', value)}
                      disabled={day.isClosed || readOnly}
                    />
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      ) : (
        /* ── Desktop: table layout ── */
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Dia</TableCell>
              {mode === 'full' && (
                <>
                  <TableCell align="center">Início</TableCell>
                  <TableCell align="center">Fim</TableCell>
                </>
              )}
              <TableCell align="center">Fechado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedule.map((day, index) => {
              const startVal = day.startTime ? dayjs(day.startTime, 'HH:mm') : null;
              const endVal = day.endTime ? dayjs(day.endTime, 'HH:mm') : null;

              return (
                <TableRow key={day.dayOfWeek}>
                  <TableCell>
                    <Box display='flex' alignItems='center' gap={0.5}>
                      {DAY_NAMES[day.dayOfWeek]}
                      {!readOnly && index < 6 && (
                        <Tooltip title="Espelhar horário para os dias seguintes">
                          <IconButton
                            size='small'
                            onClick={() => mirrorDown(index)}
                          >
                            <KeyboardDoubleArrowDown fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  {mode === 'full' && (
                    <>
                      <TableCell align="center">
                        <TimePicker
                          label="Início"
                          value={startVal}
                          onChange={(value: Dayjs | null) => handleTimeChange(index, 'startTime', value)}
                          disabled={day.isClosed || readOnly}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TimePicker
                          label="Fim"
                          value={endVal}
                          onChange={(value: Dayjs | null) => handleTimeChange(index, 'endTime', value)}
                          disabled={day.isClosed || readOnly}
                        />
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center">
                    <Checkbox
                      checked={day.isClosed}
                      onChange={e => handleDayChange(index, e.target.checked)}
                      disabled={readOnly}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {allClosed && mode === 'full' && (
        <Typography variant="body2" color="text.secondary">
          Todos os dias estão fechados. Desmarque &ldquo;Fechado&rdquo; para configurar horários.
        </Typography>
      )}

      {!readOnly && mode === 'full' && defaultStartTime && defaultEndTime && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Horário padrão: {defaultStartTime.format('HH:mm')} às {defaultEndTime.format('HH:mm')}
          </Typography>
          <Button variant="outlined" size="small" onClick={applyToAll}>
            Aplicar para todos os dias
          </Button>
        </Box>
      )}
    </Box>
  );
};
